import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Ticket, Trash2, Percent, Banknote, Gift } from 'lucide-react';
import { toast } from 'sonner';

const defaultCoupon = {
    code: '',
    discount_type: 'percent',
    discount_value: '',
    start_at: '',
    end_at: '',
    usage_limit: '',
    active: true,
};

const AdminCouponsPage = () => {
    const { api, isSuperAdmin } = useAdmin();
    const [coupons, setCoupons] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(defaultCoupon);
    const [couponToDelete, setCouponToDelete] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/coupons/analytics');
            setCoupons(res.data.coupons || []);
            setSummary(res.data.summary || null);
        } catch (e) {
            toast.error('Failed to load coupons');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCreate = async () => {
        if (!form.code.trim() || (form.discount_type !== 'free' && !form.discount_value)) {
            toast.error('Code and discount value are required');
            return;
        }
        setSaving(true);
        try {
            await api.post('/admin/coupons', {
                code: form.code.trim().toUpperCase(),
                discount_type: form.discount_type,
                discount_value: form.discount_type === 'free' ? 0 : Number(form.discount_value),
                start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
                end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
                usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
                active: form.active,
            });
            toast.success('Coupon created');
            setDialogOpen(false);
            setForm(defaultCoupon);
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create coupon');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (c) => {
        try {
            await api.put(`/admin/coupons/${c.id}`, { active: !c.active });
            fetchData();
        } catch (e) {
            toast.error('Failed to update coupon');
        }
    };

    const handleDelete = async () => {
        if (!couponToDelete) return;
        try {
            await api.delete(`/admin/coupons/${couponToDelete.id}`);
            toast.success('Coupon deleted');
            setCouponToDelete(null);
            fetchData();
        } catch (e) {
            toast.error('Failed to delete coupon');
        }
    };

    const discountLabel = (c) => {
        if (c.discount_type === 'free') return 'Free (مجاني)';
        if (c.discount_type === 'percent') return `${c.discount_value}%`;
        return `${c.discount_value} ر.س`;
    };

    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

    const stats = summary ? [
        { label: 'Total Coupons', value: summary.total_coupons, icon: Ticket },
        { label: 'Active', value: summary.active_coupons, icon: Gift },
        { label: 'Redemptions', value: summary.total_redemptions, icon: Percent },
        { label: 'Revenue (SAR)', value: summary.total_revenue_sar, icon: Banknote },
    ] : [];

    return (
        <div className="space-y-6" data-testid="admin-coupons-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Coupons (الكوبونات)</h1>
                    <p className="text-slate-400 mt-1">Discount codes for plans and pay-per-use packs</p>
                </div>
                {isSuperAdmin && (
                    <Button onClick={() => { setForm(defaultCoupon); setDialogOpen(true); }} className="bg-teal-500 hover:bg-teal-600" data-testid="create-coupon-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Coupon
                    </Button>
                )}
            </div>

            {summary && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {stats.map(({ label, value, icon: Icon }) => (
                        <Card key={label} className="border-slate-700 bg-slate-800/50">
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-teal-500/15 flex items-center justify-center">
                                    <Icon className="w-5 h-5 text-teal-400" />
                                </div>
                                <div>
                                    <p className="text-xl font-bold text-white">{value}</p>
                                    <p className="text-xs text-slate-400">{label}</p>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <Card className="border-slate-700 bg-slate-800/50">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="coupons-table">
                            <thead>
                                <tr className="border-b border-slate-700">
                                    <th className="text-left p-4 text-slate-400 font-medium">Code</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Discount</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Usage</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Validity</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Revenue</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                                    <th className="text-right p-4 text-slate-400 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={7} className="p-8 text-center text-slate-400">Loading...</td></tr>
                                ) : coupons.length === 0 ? (
                                    <tr><td colSpan={7} className="p-8 text-center text-slate-400">No coupons yet</td></tr>
                                ) : (
                                    coupons.map((c) => (
                                        <tr key={c.id} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`coupon-row-${c.code}`}>
                                            <td className="p-4">
                                                <span className="font-mono font-bold text-teal-300">{c.code}</span>
                                            </td>
                                            <td className="p-4 text-white text-sm">{discountLabel(c)}</td>
                                            <td className="p-4 text-slate-300 text-sm">
                                                {c.used_count || 0}{c.usage_limit ? ` / ${c.usage_limit}` : ' / ∞'}
                                            </td>
                                            <td className="p-4 text-slate-400 text-xs whitespace-nowrap">
                                                {fmtDate(c.start_at)} → {fmtDate(c.end_at)}
                                            </td>
                                            <td className="p-4 text-slate-300 text-sm" data-testid={`coupon-revenue-${c.code}`}>
                                                {(c.revenue_sar || 0).toFixed(2)} ر.س
                                            </td>
                                            <td className="p-4">
                                                <button
                                                    onClick={() => toggleActive(c)}
                                                    data-testid={`coupon-toggle-${c.code}`}
                                                    className={`text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${c.active
                                                        ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                                                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}
                                                >
                                                    {c.active ? 'Active' : 'Inactive'}
                                                </button>
                                            </td>
                                            <td className="p-4 text-right">
                                                {isSuperAdmin && (
                                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                                                        onClick={() => setCouponToDelete(c)} data-testid={`coupon-delete-${c.code}`}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            {/* Create dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-slate-800 border-slate-700 text-white sm:max-w-md" data-testid="coupon-dialog">
                    <DialogHeader>
                        <DialogTitle>Create Coupon</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            كود خصم يطبقه المستخدم عند الدفع
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label className="text-slate-300">Code (الكود)</Label>
                            <Input value={form.code} onChange={(e) => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                                placeholder="WELCOME20" className="bg-slate-700/50 border-slate-600 text-white font-mono" data-testid="coupon-code-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300">Type (النوع)</Label>
                                <Select value={form.discount_type} onValueChange={(v) => setForm(p => ({ ...p, discount_type: v }))}>
                                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white" data-testid="coupon-type-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-slate-800 border-slate-700">
                                        <SelectItem value="percent" className="text-white">Percent % (نسبة)</SelectItem>
                                        <SelectItem value="fixed" className="text-white">Fixed SAR (مبلغ ثابت)</SelectItem>
                                        <SelectItem value="free" className="text-white">Free (مجاني 100%)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {form.discount_type !== 'free' && (
                                <div className="space-y-2">
                                    <Label className="text-slate-300">{form.discount_type === 'percent' ? 'Percent %' : 'Amount (ر.س)'}</Label>
                                    <Input type="number" min="0" value={form.discount_value}
                                        onChange={(e) => setForm(p => ({ ...p, discount_value: e.target.value }))}
                                        className="bg-slate-700/50 border-slate-600 text-white" data-testid="coupon-value-input" />
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300">Start (بداية — اختياري)</Label>
                                <Input type="datetime-local" value={form.start_at}
                                    onChange={(e) => setForm(p => ({ ...p, start_at: e.target.value }))}
                                    className="bg-slate-700/50 border-slate-600 text-white" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-slate-300">End (نهاية — اختياري)</Label>
                                <Input type="datetime-local" value={form.end_at}
                                    onChange={(e) => setForm(p => ({ ...p, end_at: e.target.value }))}
                                    className="bg-slate-700/50 border-slate-600 text-white" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 items-end">
                            <div className="space-y-2">
                                <Label className="text-slate-300">Usage limit (حد الاستخدام الكلي)</Label>
                                <Input type="number" min="0" placeholder="∞" value={form.usage_limit}
                                    onChange={(e) => setForm(p => ({ ...p, usage_limit: e.target.value }))}
                                    className="bg-slate-700/50 border-slate-600 text-white" data-testid="coupon-limit-input" />
                            </div>
                            <div className="flex items-center gap-3 pb-1">
                                <Switch checked={form.active} onCheckedChange={(v) => setForm(p => ({ ...p, active: v }))} data-testid="coupon-active-switch" />
                                <Label className="text-slate-300">Active (مفعّل)</Label>
                            </div>
                        </div>
                        <p className="text-xs text-slate-500">كل مستخدم يستطيع استخدام الكوبون مرة واحدة فقط.</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600 text-slate-300">Cancel</Button>
                        <Button onClick={handleCreate} disabled={saving} className="bg-teal-500 hover:bg-teal-600" data-testid="coupon-save-btn">
                            {saving ? 'Saving...' : 'Create Coupon'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <AlertDialog open={!!couponToDelete} onOpenChange={(o) => !o && setCouponToDelete(null)}>
                <AlertDialogContent className="bg-slate-800 border-slate-700">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete Coupon</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            &quot;{couponToDelete?.code}&quot; will be permanently deleted.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="coupon-delete-confirm">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AdminCouponsPage;
